import { isDateInTheFuture } from '@lumapps/utils/date/isDateInTheFuture';
import { isDateInThePast } from '@lumapps/utils/date/isDateInThePast';

import { CONTENT_PUBLICATION_STATUS, type ContentPublicationStatus } from '../constants';
import { LegacyContentStatus, type Content } from '../types';

/** Get the content publication status thanks to the legacy status and his dates
 * CF: [backend code](https://github.com/lumapps/core/blob/master/core/monolite/content/use_cases/property_computations.py#L31)
 */
export const getContentPublicationStatus = ({
    legacyStatus,
    startDate,
    endDate,
    lastRevision,
    lastPublishedAt,
}: {
    legacyStatus?: LegacyContentStatus;
    startDate?: string;
    endDate?: string;
    updatedAt?: string;
    lastPublishedAt?: string | null;
    lastRevision?: Content['lastRevision'];
}) => {
    let publicationStatus: ContentPublicationStatus = CONTENT_PUBLICATION_STATUS.DRAFT;

    const hasPendingDraft =
        lastRevision?.status === LegacyContentStatus.draft && legacyStatus === LegacyContentStatus.live;

    const isAwaitingValidation = lastRevision?.status === LegacyContentStatus.toValidate;
    const isExpired = endDate && isDateInThePast(endDate);
    const isExpiringSoon =
        !isExpired &&
        legacyStatus === LegacyContentStatus.live &&
        !!endDate &&
        new Date(endDate) < new Date(Date.now() + 10 * 24 * 60 * 60 * 1000);
    const isRevisionRequested = lastRevision?.status === LegacyContentStatus.refused;

    if (legacyStatus === LegacyContentStatus.archive) {
        publicationStatus = CONTENT_PUBLICATION_STATUS.ARCHIVE;
    } else if (legacyStatus === LegacyContentStatus.live) {
        if (startDate && isDateInTheFuture(startDate)) {
            publicationStatus = CONTENT_PUBLICATION_STATUS.SCHEDULED;
        } else if (isExpired) {
            publicationStatus = CONTENT_PUBLICATION_STATUS.EXPIRED;
        } else {
            publicationStatus = CONTENT_PUBLICATION_STATUS.PUBLISHED;
        }
    } else if (legacyStatus === LegacyContentStatus.draft) {
        if (hasPendingDraft) {
            if (startDate && isDateInTheFuture(startDate)) {
                publicationStatus = CONTENT_PUBLICATION_STATUS.SCHEDULED;
            } else {
                publicationStatus = CONTENT_PUBLICATION_STATUS.PUBLISHED;
            }
        } else {
            publicationStatus = CONTENT_PUBLICATION_STATUS.DRAFT;
        }
    }

    return {
        publicationStatus,
        hasPendingDraft,
        isAwaitingValidation,
        isExpiringSoon,
        isRevisionRequested,
        expiredAt: endDate,
        scheduledAt: startDate,
        publishedAt: lastPublishedAt ?? undefined,
    };
};
